import { useEffect, useState } from 'react';
import { addressesService } from '../services/addresses';
import { addressLabel, type IndonesiaAddress } from '../types';

const DEBOUNCE_MS = 300;
const MIN_QUERY = 2;

/**
 * Search-as-you-type picker for an Indonesian kelurahan row. Holds the selected row (not
 * just its id) so the chip can restate the full label without a second lookup.
 */
const AddressPicker = ({ value, onChange }: { value: IndonesiaAddress | null; onChange: (a: IndonesiaAddress | null) => void }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<IndonesiaAddress[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const q = query.trim();
    if (q.length < MIN_QUERY) {
      setResults([]);
      setError(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      setSearching(true);
      setError(null);
      addressesService
        .search(q)
        .then((rows) => { if (!cancelled) setResults(rows); })
        .catch((e) => { if (!cancelled) setError((e as Error).message); })
        .finally(() => { if (!cancelled) setSearching(false); });
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query]);

  if (value) {
    return (
      <div className="flex items-start gap-2 text-sm">
        <span className="flex-1 bg-gray-100 border rounded px-3 py-2">{addressLabel(value)}</span>
        <button type="button" className="text-primary-600 text-xs mt-2" onClick={() => onChange(null)}>ganti</button>
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        type="text"
        aria-label="Cari kelurahan"
        className="w-full border rounded px-3 py-2 text-sm"
        placeholder="Ketik nama kelurahan (min. 2 huruf)…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {searching && <div className="text-xs text-gray-400 mt-1">Mencari…</div>}
      {error && <div className="text-red-600 text-xs mt-1">{error}</div>}
      {!searching && !error && query.trim().length >= MIN_QUERY && results.length === 0 && (
        <div className="text-xs text-gray-400 mt-1">Tidak ada hasil.</div>
      )}
      {results.length > 0 && (
        <ul className="border rounded mt-1 max-h-48 overflow-y-auto bg-white text-sm divide-y">
          {results.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                className="w-full text-left px-3 py-2 hover:bg-gray-50"
                onClick={() => { onChange(r); setQuery(''); setResults([]); }}
              >
                {addressLabel(r)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
export default AddressPicker;
